import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { PollRounded, VisibilityRounded, CloseRounded } from '@mui/icons-material';
import { SectionPicker } from './SectionPicker';
import { SurveyPreviewDialog } from './SurveyPreviewDialog';
import { surveyApi, type SurveyKind, type SurveyTemplateDto } from '../../../lib/api/survey.api';
import { errorText } from '../../../lib/error-text';

const categoryToKind = (category: string): SurveyKind =>
  category === 'end_of_course' ? 'end_of_course' : 'periodic';

interface Props {
  open: boolean;
  tenantId: string;
  templates: SurveyTemplateDto[];
  onClose: () => void;
  onLaunched: (msg: string) => void;
}

/** Slide-over to launch a survey. Section → template → title → optional close
 *  date → preview → launch. Decoupled from the management list. */
export function SurveyLaunchDrawer({
  open,
  tenantId,
  templates,
  onClose,
  onLaunched,
}: Props): JSX.Element {
  const { t } = useTranslation('sis');
  const [sectionId, setSectionId] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [title, setTitle] = useState('');
  const [closesAt, setClosesAt] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedTemplate = useMemo(
    () => templates.find((tpl) => tpl.id === templateId),
    [templates, templateId],
  );

  // Reset on open.
  useEffect(() => {
    if (open) {
      setSectionId('');
      setTemplateId('');
      setTitle('');
      setClosesAt('');
      setError(null);
    }
  }, [open]);

  // Default the title from the chosen template.
  useEffect(() => {
    if (selectedTemplate) setTitle(selectedTemplate.name);
  }, [selectedTemplate]);

  const recipientsQ = useQuery({
    queryKey: ['survey-recipient-count', tenantId, sectionId],
    queryFn: () => surveyApi.recipientCount({ tenantId, sectionId }),
    enabled: !!tenantId && !!sectionId,
  });
  const recipientCount = recipientsQ.data?.eligibleCount;

  const launchMutation = useMutation({
    mutationFn: () =>
      surveyApi.launch({
        tenantId,
        templateId,
        sectionId,
        kind: categoryToKind(selectedTemplate?.category ?? 'periodic'),
        title: title.trim() || (selectedTemplate?.name ?? ''),
        openNow: true,
        // Close date is inclusive of the chosen day → push to end of day.
        ...(closesAt ? { closesAt: `${closesAt}T23:59:59.000Z` } : {}),
      }),
    onSuccess: () => {
      onLaunched(
        recipientCount !== undefined
          ? t('surveys.launchDoneCount', { count: recipientCount })
          : t('surveys.launchDone'),
      );
      onClose();
    },
    onError: (e) => setError(errorText(e, 'error')),
  });

  const canLaunch = !!sectionId && !!templateId && !launchMutation.isPending;

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: { xs: 340, sm: 420 }, p: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6" fontWeight={700}>
            {t('surveys.launchDrawer.title')}
          </Typography>
          <Button size="small" startIcon={<CloseRounded />} onClick={onClose}>
            {t('actions.close', { ns: 'common' })}
          </Button>
        </Stack>

        <Stack spacing={2}>
          {error && <Alert severity="error">{error}</Alert>}

          <SectionPicker value={sectionId} onChange={setSectionId} />

          {sectionId && recipientCount !== undefined && (
            <Typography variant="caption" color="text.secondary">
              {t('surveys.recipients', { count: recipientCount })}
            </Typography>
          )}

          <FormControl fullWidth size="small" disabled={!sectionId}>
            <InputLabel id="launch-template">{t('surveys.template')}</InputLabel>
            <Select
              labelId="launch-template"
              label={t('surveys.template')}
              value={templateId}
              onChange={(e) => setTemplateId(e.target.value)}
            >
              {templates.map((tpl) => (
                <MenuItem key={tpl.id} value={tpl.id}>
                  {tpl.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            size="small"
            label={t('surveys.surveyTitle')}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={!templateId}
            helperText={t('surveys.surveyTitleHelp')}
          />

          <TextField
            fullWidth
            size="small"
            type="date"
            label={t('surveys.launchDrawer.closesAt')}
            value={closesAt}
            onChange={(e) => setClosesAt(e.target.value)}
            InputLabelProps={{ shrink: true }}
            helperText={t('surveys.launchDrawer.closesAtHint')}
          />

          <Divider />

          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<VisibilityRounded />}
              disabled={!selectedTemplate}
              onClick={() => setShowPreview(true)}
            >
              {t('surveys.preview')}
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button
              variant="contained"
              startIcon={
                launchMutation.isPending ? <CircularProgress size={16} /> : <PollRounded />
              }
              disabled={!canLaunch}
              onClick={() => launchMutation.mutate()}
            >
              {launchMutation.isPending ? t('surveys.launching') : t('surveys.launch')}
            </Button>
          </Stack>
        </Stack>
      </Box>

      {showPreview && selectedTemplate && (
        <SurveyPreviewDialog
          template={selectedTemplate}
          title={title}
          onClose={() => setShowPreview(false)}
        />
      )}
    </Drawer>
  );
}
